import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { BrowserStorageService } from './browser-storage-service';

export interface TimezoneInfo {
  timezone: string;
  offset: string;
  offset_seconds: number;
  abbreviation: string;
}

@Injectable({
  providedIn: 'root',
})
export class TimezoneService {
  private http = inject(HttpClient);
  private browserStorage = inject(BrowserStorageService);
  private platformId = inject(PLATFORM_ID);

  private readonly STORAGE_KEY = 'user_timezone';
  private timezoneSubject = new BehaviorSubject<string>(this.getStoredTimezone());
  timezone$ = this.timezoneSubject.asObservable();

  private cachedTimezoneList: TimezoneInfo[] | null = null;

  get currentTimezone(): string {
    return this.timezoneSubject.value;
  }

  getStoredTimezone(): string {
    return this.browserStorage.getSessionItem(this.STORAGE_KEY) || this.detectBrowserTimezone();
  }

  detectBrowserTimezone(): string {
    if (isPlatformBrowser(this.platformId)) {
      try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
      } catch {
        return 'Asia/Kolkata';
      }
    }
    return 'Asia/Kolkata';
  }

  setTimezone(tz: string): void {
    this.browserStorage.setSessionItem(this.STORAGE_KEY, tz);
    this.timezoneSubject.next(tz);
  }

  getTimezoneList(): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) {
      return of({ status: 200, count: 0, timezones: [] });
    }
    if (this.cachedTimezoneList) {
      return of({
        status: 200,
        count: this.cachedTimezoneList.length,
        timezones: this.cachedTimezoneList,
      });
    }
    return this.http.get<any>(`${environment.api}common/timezone/list/`).pipe(
      tap((res) => {
        if (res?.timezones) {
          this.cachedTimezoneList = res.timezones;
        }
      }),
      catchError(() => of({ status: 500, timezones: [] }))
    );
  }

  getUserTimezone(): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) {
      return of({ status: 500 });
    }
    return this.http.get<any>(`${environment.api}common/timezone/user-update/`).pipe(
      tap((res) => {
        if (res?.timezone?.timezone) {
          this.setTimezone(res.timezone.timezone);
        }
      }),
      catchError(() => of({ status: 500 }))
    );
  }

  updateUserTimezone(timezone: string): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) {
      return of({ status: 500 });
    }
    return this.http
      .post<any>(`${environment.api}common/timezone/user-update/`, { user_timezone: timezone })
      .pipe(
        tap((res) => {
          if (res?.status === 200) {
            this.setTimezone(timezone);
          }
        }),
        catchError(() => of({ status: 500 }))
      );
  }

  formatDateInTimezone(
    dateInput: string | Date | null,
    format: 'short' | 'medium' | 'long' | 'full' = 'medium'
  ): string {
    if (!dateInput) return 'NA';
    if (!isPlatformBrowser(this.platformId)) return String(dateInput);

    try {
      const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
      if (isNaN(date.getTime())) return 'NA';

      const tz = this.currentTimezone;
      const optionsMap: Record<string, Intl.DateTimeFormatOptions> = {
        short: {
          year: 'numeric', month: 'numeric', day: 'numeric',
          hour: 'numeric', minute: 'numeric',
          timeZone: tz,
        },
        medium: {
          year: 'numeric', month: 'short', day: 'numeric',
          hour: 'numeric', minute: 'numeric', second: 'numeric',
          timeZone: tz,
        },
        long: {
          year: 'numeric', month: 'long', day: 'numeric',
          hour: 'numeric', minute: 'numeric', second: 'numeric',
          timeZoneName: 'short',
          timeZone: tz,
        },
        full: {
          weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
          hour: 'numeric', minute: 'numeric', second: 'numeric',
          timeZoneName: 'long',
          timeZone: tz,
        },
      };

      return new Intl.DateTimeFormat('en-US', optionsMap[format] || optionsMap['medium']).format(date);
    } catch {
      return String(dateInput);
    }
  }

  formatDateOnly(dateInput: string | Date | null): string {
    if (!dateInput) return 'NA';
    if (!isPlatformBrowser(this.platformId)) return String(dateInput);

    try {
      const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
      if (isNaN(date.getTime())) return 'NA';

      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric', month: 'short', day: 'numeric',
        timeZone: this.currentTimezone,
      }).format(date);
    } catch {
      return String(dateInput);
    }
  }
}
